<template>
  <div class="h-full overflow-auto p-4 fade-in max-w-screen-2xl w-full mx-auto space-y-4">
    <!-- Page header -->
    <div class="flex items-center gap-2">
      <UserCheck class="w-5 h-5 text-gray-600" />
      <h1 class="text-xl font-normal">Onboarding</h1>
    </div>

    <!-- Onboarding instructions -->
    <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <button
        type="button"
        @click="isInstructionsExpanded = !isInstructionsExpanded"
        class="w-full flex items-center justify-between gap-2 text-left p-4"
        :class="{ 'border-b border-gray-200': isInstructionsExpanded }"
      >
        <div>
          <h2 class="text-base font-medium text-gray-800">
            <Lightbulb class="w-4 h-4 text-gray-600 inline-block mb-0.5" />
            Onboarding instructions
          </h2>
          <p class="text-sm text-gray-600">To onboard a newly signed user, find their account in the "Not Yet Onboarded" table and follow the steps below</p>
        </div>
        <ChevronDown
          class="w-5 h-5 text-gray-400 shrink-0 transition-transform duration-200"
          :class="{ '-rotate-180': isInstructionsExpanded }"
        />
      </button>
      <ol v-if="isInstructionsExpanded" class="list-decimal list-inside text-sm text-gray-600 space-y-4 p-4">
        <li>
            Click the <strong>"Actions"</strong> button and select <strong>"Assign to Pod"</strong>. Select the pod you want to assign the user to. This will automatically place them in that pod, create the user's personal Google Drive folder and place them in that pod's Google Group.
        </li>
        <li>
          Invite them to Notion by adding their email address to <a href="https://app.notion.com/p/ce0830af44c942b58719ffafd19aef73" target="_blank" class="text-blue-500">Outtalent Knowledge Base</a> with <strong>Comment rights</strong> so they can access it as Guests. (Don’t accept their requests to become a member). Mark the user's Notion checkbox in the dashboard below.
        </li>
        <li>
          Invite them to join the Outtalent Slack workspace using their email. Make sure to add them to the following channels: <code class="bg-purple-50 text-purple-700 px-1 rounded-md">#2026-superpod-x (their superpod name), #compensation, #enrolledfellows, #interviewgym, #resumefeedback, #interviewswatch, #introyourself, #photos, #random, #social-boost</code>. Mark the user's Slack checkbox in the dashboard below.
        </li>
        <li>
          Invite the fellow to the <a href="https://airtable.com/appkJCzC2SrsAHHqC/tblkcZEnusYmcvKtY/viwMWVal0XNkM330M?blocks=hide" target="_blank" class="text-blue-500">Interview Gym Airtable</a> with <strong>read access only</strong>.
        </li>
        <li>
          Download their signed contract from HelloSign and place it in the right ISA folder (make sure the year is correct). Share <strong>“Comment” access</strong> to the file with the fellow. Name the file <strong>"YYYY-MM-DD ISA_Fellow_Name"</strong>.
        </li>
        <li>
          Finally, in the dashboard below click the <strong>"Actions"</strong> button and select <strong>"Send welcome email"</strong>.
        </li>
      </ol>
    </div>

    <!-- Not Yet Onboarded -->
    <section class="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div class="flex items-center justify-between px-5 py-3 border-b border-gray-100">
        <div class="flex items-center gap-2">
          <h2 class="text-base font-medium text-gray-800">Not Yet Onboarded</h2>
          <span
            v-if="!isLoadingNotOnboarded"
            class="text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5"
          >{{ notOnboardedUsers.length }}</span>
        </div>
        <button
          @click="copyEmails(notOnboardedUsers)"
          class="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 transition-colors"
        >
          <Copy class="w-3.5 h-3.5" />
          Copy Emails
        </button>
      </div>

      <div v-if="isLoadingNotOnboarded" class="py-12 flex justify-center">
        <Loader2 class="w-5 h-5 animate-spin text-gray-400" />
      </div>

      <div v-else-if="notOnboardedUsers.length === 0" class="py-10 text-center text-sm text-gray-400">
        No users pending onboarding.
      </div>

      <div v-else class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-xs text-gray-500 bg-gray-50 border-b border-gray-100">
              <th class="px-4 py-2.5 text-left font-medium">User</th>
              <th class="px-4 py-2.5 text-center font-medium">Notion</th>
              <th class="px-4 py-2.5 text-center font-medium">Slack</th>
              <th class="px-4 py-2.5 text-center font-medium">Google Group</th>
              <th class="px-4 py-2.5 text-center font-medium">Pod</th>
              <th class="px-4 py-2.5 text-center font-medium">Onboarding Email</th>
              <th class="px-4 py-2.5 text-center font-medium">Welcome Email</th>
              <th class="px-4 py-2.5 text-left font-medium">Slack Member ID</th>
              <th class="px-4 py-2.5 text-center font-medium">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-50">
            <tr
              v-for="user in notOnboardedUsers"
              :key="user.id"
              class="hover:bg-gray-50/50 transition-colors"
            >
              <!-- User info -->
              <td class="px-4 py-3">
                <div class="flex items-center gap-2.5 min-w-[180px]">
                  <div class="w-8 h-8 rounded-full overflow-hidden bg-gray-100 shrink-0 flex items-center justify-center">
                    <img v-if="user.avatar_image" :src="user.avatar_image" :alt="user.first_name" class="w-full h-full object-cover" />
                    <span v-else class="text-xs font-medium text-gray-500 uppercase">
                      {{ (user.first_name?.[0] || '') + (user.last_name?.[0] || '') }}
                    </span>
                  </div>
                  <div class="min-w-0">
                    <p class="text-sm font-medium text-gray-900 leading-tight truncate">{{ user.first_name }} {{ user.last_name }}</p>
                    <p class="text-xs text-gray-400 truncate">{{ user.email }}</p>
                  </div>
                </div>
              </td>

              <!-- Checklist booleans -->
              <td class="px-4 py-3 text-center">
                <button
                  @click="updateField(user, 'added_to_notion', !user.checklist.added_to_notion)"
                  :disabled="checklistLoading[user.id]?.added_to_notion"
                  :title="flagTitle('Notion', user.checklist.added_to_notion_at)"
                  class="inline-flex items-center justify-center w-5 h-5 rounded border transition-colors mx-auto"
                  :class="user.checklist.added_to_notion ? 'bg-green-500 border-green-500 text-white' : 'bg-white border-gray-300 hover:border-gray-400'"
                >
                  <Loader2 v-if="checklistLoading[user.id]?.added_to_notion" class="w-3 h-3 animate-spin text-gray-400" />
                  <Check v-else-if="user.checklist.added_to_notion" class="w-3 h-3" />
                </button>
              </td>
              <td class="px-4 py-3 text-center">
                <button
                  @click="updateField(user, 'added_to_slack', !user.checklist.added_to_slack)"
                  :disabled="checklistLoading[user.id]?.added_to_slack"
                  :title="flagTitle('Slack', user.checklist.added_to_slack_at)"
                  class="inline-flex items-center justify-center w-5 h-5 rounded border transition-colors mx-auto"
                  :class="user.checklist.added_to_slack ? 'bg-green-500 border-green-500 text-white' : 'bg-white border-gray-300 hover:border-gray-400'"
                >
                  <Loader2 v-if="checklistLoading[user.id]?.added_to_slack" class="w-3 h-3 animate-spin text-gray-400" />
                  <Check v-else-if="user.checklist.added_to_slack" class="w-3 h-3" />
                </button>
              </td>
              <td class="px-4 py-3 text-center">
                <button
                  @click="updateField(user, 'added_to_google_group', !user.checklist.added_to_google_group)"
                  :disabled="checklistLoading[user.id]?.added_to_google_group"
                  :title="flagTitle('Google Group', user.checklist.added_to_google_group_at)"
                  class="inline-flex items-center justify-center w-5 h-5 rounded border transition-colors mx-auto"
                  :class="user.checklist.added_to_google_group ? 'bg-green-500 border-green-500 text-white' : 'bg-white border-gray-300 hover:border-gray-400'"
                >
                  <Loader2 v-if="checklistLoading[user.id]?.added_to_google_group" class="w-3 h-3 animate-spin text-gray-400" />
                  <Check v-else-if="user.checklist.added_to_google_group" class="w-3 h-3" />
                </button>
              </td>
              <td class="px-4 py-3 text-center">
                <button
                  @click="updateField(user, 'added_to_pod', !user.checklist.added_to_pod)"
                  :disabled="checklistLoading[user.id]?.added_to_pod"
                  :title="flagTitle('Pod', user.checklist.added_to_pod_at)"
                  class="inline-flex items-center justify-center w-5 h-5 rounded border transition-colors mx-auto"
                  :class="user.checklist.added_to_pod ? 'bg-green-500 border-green-500 text-white' : 'bg-white border-gray-300 hover:border-gray-400'"
                >
                  <Loader2 v-if="checklistLoading[user.id]?.added_to_pod" class="w-3 h-3 animate-spin text-gray-400" />
                  <Check v-else-if="user.checklist.added_to_pod" class="w-3 h-3" />
                </button>
              </td>
              <td class="px-4 py-3 text-center">
                <button
                  @click="updateField(user, 'sent_onboarding_email', !user.checklist.sent_onboarding_email)"
                  :disabled="checklistLoading[user.id]?.sent_onboarding_email"
                  class="inline-flex items-center justify-center w-5 h-5 rounded border transition-colors mx-auto"
                  :class="user.checklist.sent_onboarding_email ? 'bg-green-500 border-green-500 text-white' : 'bg-white border-gray-300 hover:border-gray-400'"
                >
                  <Loader2 v-if="checklistLoading[user.id]?.sent_onboarding_email" class="w-3 h-3 animate-spin text-gray-400" />
                  <Check v-else-if="user.checklist.sent_onboarding_email" class="w-3 h-3" />
                </button>
                <p v-if="user.checklist.sent_onboarding_email_at" class="text-[10px] text-gray-400 mt-1">{{ fmtDate(user.checklist.sent_onboarding_email_at) }}</p>
              </td>
              <!-- Welcome Email (toggle) -->
              <td class="px-4 py-3 text-center">
                <button
                  @click="updateField(user, 'sent_welcome_aboard_email', !user.checklist.sent_welcome_aboard_email)"
                  :disabled="checklistLoading[user.id]?.sent_welcome_aboard_email"
                  :title="flagTitle('Welcome Email', user.checklist.sent_welcome_aboard_email_at)"
                  class="inline-flex items-center justify-center w-5 h-5 rounded border transition-colors mx-auto"
                  :class="user.checklist.sent_welcome_aboard_email ? 'bg-green-500 border-green-500 text-white' : 'bg-white border-gray-300 hover:border-gray-400'"
                >
                  <Loader2 v-if="checklistLoading[user.id]?.sent_welcome_aboard_email" class="w-3 h-3 animate-spin text-gray-400" />
                  <Check v-else-if="user.checklist.sent_welcome_aboard_email" class="w-3 h-3" />
                </button>
                <p v-if="user.checklist.sent_welcome_aboard_email_at" class="text-[10px] text-gray-400 mt-1">{{ fmtDate(user.checklist.sent_welcome_aboard_email_at) }}</p>
              </td>
              <!-- Slack Member ID (display only) -->
              <td class="px-4 py-3">
                <SlackIdDisplay :slack-member-id="user.slack_member_id" />
              </td>
              <!-- Actions -->
              <td class="px-4 py-3 text-center">
                <OnboardingActionsMenu
                  :welcome-ready="allChecklistDone(user)"
                  :welcome-sent="user.checklist.sent_welcome_aboard_email"
                  :has-slack-id="!!user.slack_member_id"
                  :sending="sendingWelcome[user.id] || false"
                  :pods="pods"
                  @send-welcome="doSendWelcome(user)"
                  @edit-slack="editSlack(user)"
                  @assign-pod="(podId) => assignToPod(user, podId)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- Latest Pod -->
    <section class="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div class="flex items-center justify-between px-5 py-3 border-b border-gray-100">
        <div class="flex items-center gap-2">
          <h2 class="text-base font-medium text-gray-800">
            Latest Pod
            <span v-if="latestPod" class="font-normal text-gray-500 ml-1">— {{ latestPod.name }}</span>
          </h2>
          <span
            v-if="!isLoadingLatestPod"
            class="text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5"
          >{{ latestPodUsers.length }}</span>
        </div>
        <button
          @click="copyEmails(latestPodUsers)"
          class="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 transition-colors"
        >
          <Copy class="w-3.5 h-3.5" />
          Copy Emails
        </button>
      </div>

      <div v-if="isLoadingLatestPod" class="py-12 flex justify-center">
        <Loader2 class="w-5 h-5 animate-spin text-gray-400" />
      </div>

      <div v-else-if="!latestPod || latestPodUsers.length === 0" class="py-10 text-center text-sm text-gray-400">
        No users in the latest pod.
      </div>

      <div v-else class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-xs text-gray-500 bg-gray-50 border-b border-gray-100">
              <th class="px-4 py-2.5 text-left font-medium">User</th>
              <th class="px-4 py-2.5 text-center font-medium">Notion</th>
              <th class="px-4 py-2.5 text-center font-medium">Slack</th>
              <th class="px-4 py-2.5 text-center font-medium">Google Group</th>
              <th class="px-4 py-2.5 text-center font-medium">Pod</th>
              <th class="px-4 py-2.5 text-center font-medium">Onboarding Email</th>
              <th class="px-4 py-2.5 text-center font-medium">Welcome Email</th>
              <th class="px-4 py-2.5 text-left font-medium">Slack Member ID</th>
              <th class="px-4 py-2.5 text-center font-medium">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-50">
            <tr
              v-for="user in latestPodUsers"
              :key="user.id"
              class="hover:bg-gray-50/50 transition-colors"
            >
              <td class="px-4 py-3">
                <div class="flex items-center gap-2.5 min-w-[180px]">
                  <div class="w-8 h-8 rounded-full overflow-hidden bg-gray-100 shrink-0 flex items-center justify-center">
                    <img v-if="user.avatar_image" :src="user.avatar_image" :alt="user.first_name" class="w-full h-full object-cover" />
                    <span v-else class="text-xs font-medium text-gray-500 uppercase">
                      {{ (user.first_name?.[0] || '') + (user.last_name?.[0] || '') }}
                    </span>
                  </div>
                  <div class="min-w-0">
                    <p class="text-sm font-medium text-gray-900 leading-tight truncate">{{ user.first_name }} {{ user.last_name }}</p>
                    <p class="text-xs text-gray-400 truncate">{{ user.email }}</p>
                  </div>
                </div>
              </td>
              <td class="px-4 py-3 text-center">
                <button
                  @click="updateField(user, 'added_to_notion', !user.checklist.added_to_notion)"
                  :disabled="checklistLoading[user.id]?.added_to_notion"
                  :title="flagTitle('Notion', user.checklist.added_to_notion_at)"
                  class="inline-flex items-center justify-center w-5 h-5 rounded border transition-colors mx-auto"
                  :class="user.checklist.added_to_notion ? 'bg-green-500 border-green-500 text-white' : 'bg-white border-gray-300 hover:border-gray-400'"
                >
                  <Loader2 v-if="checklistLoading[user.id]?.added_to_notion" class="w-3 h-3 animate-spin text-gray-400" />
                  <Check v-else-if="user.checklist.added_to_notion" class="w-3 h-3" />
                </button>
              </td>
              <td class="px-4 py-3 text-center">
                <button
                  @click="updateField(user, 'added_to_slack', !user.checklist.added_to_slack)"
                  :disabled="checklistLoading[user.id]?.added_to_slack"
                  :title="flagTitle('Slack', user.checklist.added_to_slack_at)"
                  class="inline-flex items-center justify-center w-5 h-5 rounded border transition-colors mx-auto"
                  :class="user.checklist.added_to_slack ? 'bg-green-500 border-green-500 text-white' : 'bg-white border-gray-300 hover:border-gray-400'"
                >
                  <Loader2 v-if="checklistLoading[user.id]?.added_to_slack" class="w-3 h-3 animate-spin text-gray-400" />
                  <Check v-else-if="user.checklist.added_to_slack" class="w-3 h-3" />
                </button>
              </td>
              <td class="px-4 py-3 text-center">
                <button
                  @click="updateField(user, 'added_to_google_group', !user.checklist.added_to_google_group)"
                  :disabled="checklistLoading[user.id]?.added_to_google_group"
                  :title="flagTitle('Google Group', user.checklist.added_to_google_group_at)"
                  class="inline-flex items-center justify-center w-5 h-5 rounded border transition-colors mx-auto"
                  :class="user.checklist.added_to_google_group ? 'bg-green-500 border-green-500 text-white' : 'bg-white border-gray-300 hover:border-gray-400'"
                >
                  <Loader2 v-if="checklistLoading[user.id]?.added_to_google_group" class="w-3 h-3 animate-spin text-gray-400" />
                  <Check v-else-if="user.checklist.added_to_google_group" class="w-3 h-3" />
                </button>
              </td>
              <td class="px-4 py-3 text-center">
                <button
                  @click="updateField(user, 'added_to_pod', !user.checklist.added_to_pod)"
                  :disabled="checklistLoading[user.id]?.added_to_pod"
                  :title="flagTitle('Pod', user.checklist.added_to_pod_at)"
                  class="inline-flex items-center justify-center w-5 h-5 rounded border transition-colors mx-auto"
                  :class="user.checklist.added_to_pod ? 'bg-green-500 border-green-500 text-white' : 'bg-white border-gray-300 hover:border-gray-400'"
                >
                  <Loader2 v-if="checklistLoading[user.id]?.added_to_pod" class="w-3 h-3 animate-spin text-gray-400" />
                  <Check v-else-if="user.checklist.added_to_pod" class="w-3 h-3" />
                </button>
              </td>
              <td class="px-4 py-3 text-center">
                <button
                  @click="updateField(user, 'sent_onboarding_email', !user.checklist.sent_onboarding_email)"
                  :disabled="checklistLoading[user.id]?.sent_onboarding_email"
                  class="inline-flex items-center justify-center w-5 h-5 rounded border transition-colors mx-auto"
                  :class="user.checklist.sent_onboarding_email ? 'bg-green-500 border-green-500 text-white' : 'bg-white border-gray-300 hover:border-gray-400'"
                >
                  <Loader2 v-if="checklistLoading[user.id]?.sent_onboarding_email" class="w-3 h-3 animate-spin text-gray-400" />
                  <Check v-else-if="user.checklist.sent_onboarding_email" class="w-3 h-3" />
                </button>
                <p v-if="user.checklist.sent_onboarding_email_at" class="text-[10px] text-gray-400 mt-1">{{ fmtDate(user.checklist.sent_onboarding_email_at) }}</p>
              </td>
              <!-- Welcome Email (toggle) -->
              <td class="px-4 py-3 text-center">
                <button
                  @click="updateField(user, 'sent_welcome_aboard_email', !user.checklist.sent_welcome_aboard_email)"
                  :disabled="checklistLoading[user.id]?.sent_welcome_aboard_email"
                  :title="flagTitle('Welcome Email', user.checklist.sent_welcome_aboard_email_at)"
                  class="inline-flex items-center justify-center w-5 h-5 rounded border transition-colors mx-auto"
                  :class="user.checklist.sent_welcome_aboard_email ? 'bg-green-500 border-green-500 text-white' : 'bg-white border-gray-300 hover:border-gray-400'"
                >
                  <Loader2 v-if="checklistLoading[user.id]?.sent_welcome_aboard_email" class="w-3 h-3 animate-spin text-gray-400" />
                  <Check v-else-if="user.checklist.sent_welcome_aboard_email" class="w-3 h-3" />
                </button>
                <p v-if="user.checklist.sent_welcome_aboard_email_at" class="text-[10px] text-gray-400 mt-1">{{ fmtDate(user.checklist.sent_welcome_aboard_email_at) }}</p>
              </td>
              <!-- Slack Member ID (display only) -->
              <td class="px-4 py-3">
                <SlackIdDisplay :slack-member-id="user.slack_member_id" />
              </td>
              <!-- Actions -->
              <td class="px-4 py-3 text-center">
                <OnboardingActionsMenu
                  :welcome-ready="allChecklistDone(user)"
                  :welcome-sent="user.checklist.sent_welcome_aboard_email"
                  :has-slack-id="!!user.slack_member_id"
                  :sending="sendingWelcome[user.id] || false"
                  :pods="pods"
                  @send-welcome="doSendWelcome(user)"
                  @edit-slack="editSlack(user)"
                  @assign-pod="(podId) => assignToPod(user, podId)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- Copy success toast -->
    <div
      v-if="copySuccess"
      class="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-4 py-2.5 rounded-lg shadow-lg flex items-center gap-2 animate-fade-in z-50"
    >
      <Check class="w-3.5 h-3.5 text-green-400" />
      Emails copied to clipboard
    </div>

    <!-- Pod assignment progress overlay -->
    <Teleport to="body">
    <Transition name="fade">
      <div
        v-if="assigningUserId !== null"
        class="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm"
      >
        <div class="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-sm mx-4">
          <h2 class="text-base font-semibold text-gray-900 mb-1">Onboarding in progress</h2>
          <p class="text-xs text-gray-400 mb-6">Setting up the fellow's access. Please wait…</p>

          <ul class="space-y-3">
            <li v-for="step in assignSteps" :key="step.id" class="flex items-center gap-3">
              <div
class="w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-colors duration-300"
                :class="{
                  'bg-green-100': step.state === 'done',
                  'bg-red-100': step.state === 'failed',
                  'bg-cosmicPurple/10': step.state === 'active',
                  'bg-gray-100': step.state === 'pending',
                }"
              >
                <Check v-if="step.state === 'done'" class="w-3.5 h-3.5 text-green-600" />
                <X v-else-if="step.state === 'failed'" class="w-3.5 h-3.5 text-red-500" />
                <Loader2 v-else-if="step.state === 'active'" class="w-3.5 h-3.5 text-cosmicPurple animate-spin" />
                <component v-else :is="step.icon" class="w-3.5 h-3.5 text-gray-300" />
              </div>
              <div class="min-w-0">
                <p
class="text-sm leading-tight transition-colors duration-300"
                  :class="{
                    'text-gray-800 font-medium': step.state === 'done',
                    'text-red-600 font-medium': step.state === 'failed',
                    'text-cosmicPurple font-medium': step.state === 'active',
                    'text-gray-300': step.state === 'pending',
                  }"
                >{{ step.label }}</p>
                <p v-if="step.state === 'active'" class="text-[11px] text-gray-400 mt-0.5">{{ step.detail }}</p>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>

<script setup lang="ts">
import { ref, reactive, onMounted, watch, h, defineComponent } from 'vue'
import { UserCheck, Copy, Loader2, Check, X, FolderOpen, Users, Mail, Lightbulb, ChevronDown } from 'lucide-vue-next'
import { useOnboardingStore, type OnboardingUser } from '@/stores/onboarding'
import { storeToRefs } from 'pinia'
import type { Component } from 'vue'
import { toast } from '@/components/ui/toast/use-toast'
import OnboardingActionsMenu from '@/components/onboarding/OnboardingActionsMenu.vue'

const store = useOnboardingStore()
const {
  notOnboardedUsers,
  latestPodUsers,
  latestPod,
  pods,
  isLoadingNotOnboarded,
  isLoadingLatestPod,
} = storeToRefs(store)

// ── Onboarding instructions collapse state ────────────────────────────────
const INSTRUCTIONS_EXPANDED_KEY = 'onboarding-instructions-expanded'
const isInstructionsExpanded = ref(localStorage.getItem(INSTRUCTIONS_EXPANDED_KEY) !== 'false')

watch(isInstructionsExpanded, (value) => {
  localStorage.setItem(INSTRUCTIONS_EXPANDED_KEY, String(value))
})

// ── Date helpers ──────────────────────────────────────────────────────────
function fmtDate(iso: string | null): string {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

function flagTitle(label: string, iso: string | null): string {
  return iso ? `${label} — set ${fmtDate(iso)}` : `${label} — not set`
}

function allChecklistDone(user: OnboardingUser): boolean {
  const c = user.checklist
  return c.added_to_notion && c.added_to_slack && c.added_to_google_group && c.added_to_pod && c.sent_onboarding_email
}

// ── Actions: send welcome email ───────────────────────────────────────────
const sendingWelcome = reactive<Record<number, boolean>>({})

async function doSendWelcome(user: OnboardingUser) {
  if (user.checklist.sent_welcome_aboard_email) return
  if (!window.confirm(
    `Send the Welcome Aboard email to ${user.first_name} ${user.last_name}? This marks them fully onboarded.`,
  )) return

  sendingWelcome[user.id] = true
  try {
    await store.sendWelcomeEmail(user.id)
    toast({
      title: 'Welcome email sent',
      description: `${user.first_name} ${user.last_name} is now fully onboarded.`,
    })
    await store.fetchNotOnboarded()
  } catch {
    toast({
      variant: 'destructive',
      title: 'Failed to send welcome email',
      description: `Could not send the welcome email to ${user.first_name} ${user.last_name}. Please try again.`,
    })
  } finally {
    sendingWelcome[user.id] = false
  }
}

// ── Actions: Slack Member ID ──────────────────────────────────────────────
async function editSlack(user: OnboardingUser) {
  const current = user.slack_member_id ?? ''
  const value = window.prompt(`Slack Member ID for ${user.first_name} ${user.last_name}`, current)
  if (value === null) return // cancelled
  const raw = value.trim()
  const match = raw.match(/\/([A-Z0-9]+)\s*$/)
  const trimmed = match ? match[1] : raw
  if (trimmed === current) return
  try {
    await store.updateSlackId(user.id, trimmed)
    toast({
      title: 'Slack ID saved',
      description: `${user.first_name} ${user.last_name} → ${trimmed || '(cleared)'}`,
    })
  } catch {
    toast({
      variant: 'destructive',
      title: 'Failed to save Slack ID',
      description: `Could not update Slack ID for ${user.first_name} ${user.last_name}. Please try again.`,
    })
  }
}

// ── Inline display cells ──────────────────────────────────────────────────
const SlackIdDisplay = defineComponent({
  props: { slackMemberId: { type: String as () => string | null, default: null } },
  setup(props) {
    return () => props.slackMemberId
      ? h('span', { class: 'text-xs font-mono text-gray-700' }, props.slackMemberId)
      : h('span', { class: 'text-xs text-gray-300' }, '—')
  },
})

// Checklist loading state: userId -> field -> boolean
const checklistLoading = reactive<Record<number, Record<string, boolean>>>({})

const copySuccess = ref(false)

// ── Assignment progress overlay ───────────────────────────────────────────
const assigningUserId = ref<number | null>(null)

type StepState = 'pending' | 'active' | 'done' | 'failed'
interface Step { id: string; label: string; detail: string; icon: Component; state: StepState; delay: number }

const assignSteps = reactive<Step[]>([
  { id: 'drive',  label: 'Creating Google Drive folder', detail: 'Setting up personal folder…',       icon: FolderOpen, state: 'pending', delay: 0 },
  { id: 'group',  label: 'Inviting to Google Group',     detail: 'Adding to group email…',             icon: Users,      state: 'pending', delay: 3000 },
  { id: 'email',  label: 'Sending onboarding email',     detail: 'Sending welcome email…',             icon: Mail,       state: 'pending', delay: 7000 },
])

let stepTimers: ReturnType<typeof setTimeout>[] = []

function startAssignProgress() {
  assignSteps.forEach((s) => (s.state = 'pending'))
  stepTimers = []
  assignSteps.forEach((step, i) => {
    stepTimers.push(setTimeout(() => {
      step.state = 'active'
      if (i > 0) assignSteps[i - 1].state = 'done'
    }, step.delay))
  })
}

function completeAssignProgress() {
  stepTimers.forEach(clearTimeout)
  stepTimers = []
  assignSteps.forEach((s) => (s.state = 'done'))
}

function resetAssignProgress() {
  stepTimers.forEach(clearTimeout)
  stepTimers = []
  assignSteps.forEach((s) => (s.state = 'pending'))
}

type ChecklistBoolField = 'added_to_notion' | 'added_to_slack' | 'added_to_google_group' | 'added_to_pod' | 'sent_onboarding_email' | 'sent_welcome_aboard_email'

function setChecklistLoading(userId: number, field: string, value: boolean) {
  if (!checklistLoading[userId]) checklistLoading[userId] = {}
  checklistLoading[userId][field] = value
}

async function updateField(user: OnboardingUser, field: ChecklistBoolField, value: boolean) {
  setChecklistLoading(user.id, field, true)
  try {
    await store.updateChecklist(user.id, { [field]: value })
  } finally {
    setChecklistLoading(user.id, field, false)
  }
}

async function assignToPod(user: OnboardingUser, podId: number) {
  assigningUserId.value = user.id
  startAssignProgress()
  try {
    await store.assignPod([user.id], podId)
    completeAssignProgress()
    await new Promise((r) => setTimeout(r, 800))
    await Promise.all([store.fetchNotOnboarded(), store.fetchLatestPodUsers()])
  } catch {
    resetAssignProgress()
  } finally {
    assigningUserId.value = null
  }
}

function copyEmails(users: OnboardingUser[]) {
  const emails = users.map((u) => u.email).join(', ')
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  navigator.clipboard.writeText(emails).then(() => {
    copySuccess.value = true
    setTimeout(() => {
      copySuccess.value = false
    }, 2500)
  })
}

onMounted(async () => {
  await Promise.all([
    store.fetchNotOnboarded(),
    store.fetchLatestPodUsers(),
    store.fetchPods(),
  ])
})
</script>
